import { MAIN_WINDOW_LABEL } from '@/tauri/window-labels';
import { chronicleLogger } from '@/utils/logger/chronicle-logger';
import { logger } from '@/utils/logger/logger';
import { getCurrentWindow } from '@tauri-apps/api/window';

/**
 * Caches the first (in-flight or resolved) attach so repeat invocations are
 * no-ops — see {@link attachTauriRustLogsToLogger}. Reset to `null` if the
 * first attempt fails so a later call can retry.
 */
let attachPromise: Promise<void> | null = null;

/**
 * Attaches Rust-side Tauri logs (via @tauri-apps/plugin-log) to our frontend
 * logger, exactly once per JS context and only in the main window.
 *
 * The plugin's `Webview` target broadcasts every Rust log line to **all**
 * webviews (main, dictation widget, RPA overlay/toolbar), so the main window
 * is the single designated forwarder — attaching in auxiliary windows too
 * would submit each Rust line to Datadog once per open window.
 *
 * Idempotency is load-bearing (APP-6067): `attachLogger` calls Tauri's
 * `listen('log://log', …)`, which does **not** dedupe — every call appends
 * another listener, and each Rust `tracing` log line then fans out to Datadog
 * once per listener (observed as ~N× duplication, inflating log volume and
 * incident counts). Caching the first call's promise guarantees a single
 * listener no matter how often (or from where) this is invoked, and shares the
 * in-flight promise across concurrent callers.
 *
 * This ensures:
 * - Correct log levels (trace/debug/info/warn/error)
 * - Stable Datadog filtering via a structured `runtime: tauri-rust` context field
 * - Dictation logs get `module: dictation` metadata instead of relying on a
 *   message prefix for filtering
 * - Offline recording logs get `module: offline_recording` metadata across
 *   raw, development, and packaged-build log formats
 * - Chronicle / Heidi Remote logs (BLE, bulk sync, session engine, …) get
 *   `module: chronicle` plus a live `heidi_remote_device.*` device snapshot
 *   (APP-6285), so device identity / signal can be correlated with the
 *   bulk-sync flow without tagging unrelated logs.
 * - RPA telemetry lines (`[rpa]` prefix) get `module: rpa` plus their
 *   `key=value` fields parsed into queryable Datadog attributes.
 * - Any line declaring `tag="..."` gets the same `key=value` parsing on
 *   whichever logger it routes to, so structured telemetry is queryable as
 *   `@tag:… @chunk_index:…` instead of only as free text.
 * - Chronicle / Remote packaged JSON envelopes are unwrapped for Datadog:
 *   the inner tracing `message` becomes `{ msg }`, and `file` / `line` /
 *   `target` / `module_path` are promoted as `rust_*` attributes.
 * - Free-form lines (no module prefix, no `tag="..."`) below warn are dropped
 *   from transport and counted — they can carry identifiers the
 *   path sanitizer does not cover.
 * - Every forwarded line is capped at {@link MAX_FORWARDED_LENGTH} characters,
 *   applied to the routed message rather than to a packaged JSON envelope.
 */
export function attachTauriRustLogsToLogger(): Promise<void> {
  if (getCurrentWindow().label !== MAIN_WINDOW_LABEL) return Promise.resolve();
  if (attachPromise) return attachPromise;
  attachPromise = attachRustLogsToLoggerOnce().catch((err) => {
    // No listener is attached on failure, so let a later call retry.
    attachPromise = null;
    throw err;
  });
  return attachPromise;
}

async function attachRustLogsToLoggerOnce(): Promise<void> {
  const { attachLogger, LogLevel } = await import('@tauri-apps/plugin-log');

  const tauriRustLogger = logger.child({ runtime: 'tauri-rust' });
  // Hoisted: the binding is constant, so the child is built once at attach
  // rather than per forwarded free-form line.
  const tauriFreeFormLogger = tauriRustLogger.child({ rust_freeform: true });
  const tauriDictationLogger = logger.child({
    runtime: 'tauri-rust',
    module: 'dictation',
  });
  const tauriOfflineRecordingLogger = logger.child({
    runtime: 'tauri-rust',
    module: 'offline_recording',
  });
  const tauriRpaLogger = logger.child({
    runtime: 'tauri-rust',
    module: 'rpa',
  });
  const tauriChronicleLogger = chronicleLogger.child({ runtime: 'tauri-rust' });

  await attachLogger(({ level, message }) => {
    const { logMessage, rustLogger, isChronicle, envelopeFields } = getRustLogTarget(
      message,
      {
        tauriRustLogger,
        tauriDictationLogger,
        tauriOfflineRecordingLogger,
        tauriRpaLogger,
        tauriChronicleLogger,
      }
    );

    // A free-form line has no module prefix, no routable target and no tag=
    // telemetry, so nothing identifies what it is or which subsystem owns it.
    // This is insurance against UNKNOWN and FUTURE lines, not against any
    // known one: `[BLE]` scan results route via the chronicle prefix and never
    // reach here, and bystander peripheral names and user SSIDs are redacted
    // at source by an earlier privacy change, and by connection.rs here. What is
    // left is prose nobody has classified, which may carry identifiers the
    // path sanitizer does not cover. Below warn it is dropped from transport
    // and counted; warn/error still forward as the incident signal.
    let targetLogger = rustLogger;
    if (targetLogger === null) {
      if (level !== LogLevel.Warn && level !== LogLevel.Error) {
        countDroppedFreeFormRustLine(tauriRustLogger);
        return;
      }
      // A packaged envelope's metadata rode in the raw text before; carry it as
      // bindings instead, the way the module routes already do.
      targetLogger = withEnvelopeFields(tauriFreeFormLogger, envelopeFields ?? {});
    }

    // Cap every forwarded route, not just the free-form one, so an unexpected
    // payload dump cannot ride any line. Applied to the routed message: capping
    // the raw packaged envelope instead sliced the JSON, not the message.
    const forwarded = logMessage.slice(0, MAX_FORWARDED_LENGTH);

    // Chronicle logs are passed as an object so the device snapshot can attach
    // (the chronicle logger only enriches object payloads, not raw strings).
    const payload: unknown = isChronicle ? { msg: forwarded } : forwarded;

    switch (level) {
      case LogLevel.Trace:
        targetLogger.trace(payload);
        break;
      case LogLevel.Debug:
        targetLogger.debug(payload);
        break;
      case LogLevel.Info:
        targetLogger.info(payload);
        break;
      case LogLevel.Warn:
        targetLogger.warn(payload);
        break;
      case LogLevel.Error:
        targetLogger.error(payload);
        break;
    }
  });
}

type RustLogger = ReturnType<typeof logger.child>;

const DROPPED_LINE_SUMMARY_INTERVAL = 100;

const MAX_FORWARDED_LENGTH = 500;

let droppedFreeFormRustLines = 0;

/** Test-only visibility into the free-form drop counter. */
export const getDroppedFreeFormRustLineCount = (): number => droppedFreeFormRustLines;

const countDroppedFreeFormRustLine = (tauriRustLogger: RustLogger): void => {
  droppedFreeFormRustLines += 1;
  if (droppedFreeFormRustLines % DROPPED_LINE_SUMMARY_INTERVAL === 0) {
    tauriRustLogger.info({
      msg: 'Dropped free-form Rust log lines from transport',
      dropped_freeform_rust_lines: droppedFreeFormRustLines,
    });
  }
};

const DICTATION_PREFIX = '[Dictation]';

const OFFLINE_RECORDING_PREFIXES = [
  '[offline_recording::',
  '[offline_recording]',
  '[offline-recording]',
  '[connectivity]',
];

/**
 * Message prefixes the Rust Chronicle / Heidi Remote subsystems emit. Matched by
 * `startsWith` (same approach as the dictation prefix). New Chronicle log
 * prefixes must be added here to carry the `heidi_remote_device` snapshot and
 * get packaged JSON unwrapped for Datadog display. A prefix missing here (or
 * from the other prefix lists) makes the line free-form, and free-form lines
 * below warn are dropped from transport — new Rust prefixes must be mirrored
 * into these lists or their info/debug/trace logs silently vanish.
 *
 * Source of truth: `rg -o '"\[[^"]+\]' tauri-plugin-chronicle
 * src-tauri/src/chronicle src-tauri/src/session_creation_engine`
 *
 * Do not use `[connect` as a stem — it would steal offline `[connectivity]`.
 */
const CHRONICLE_PREFIXES = [
  '[Chronicle USB]',
  '[Chronicle]',
  '[chronicle', // `[chronicle]`, `[chronicle/setup_state]`, …
  '[BLE]',
  '[bulk_download', // `[bulk_download]`, `[bulk_download/chunked]`, `[bulk_download_probe]`
  '[bulk_sync]',
  '[session_engine]',
  '[auto_bulk_sync]',
  '[auto_connect]',
  '[auto_session]',
  '[DeviceMonitor]',
  '[DeviceManager]',
  '[storage]',
  '[connect]', // exact — not `[connect` (would match `[connectivity]`)
  '[connect_via_tiers]',
  '[connect_with_escalation]',
  '[decryptor]',
  '[dual_try]',
  '[ensure_connected_idle]',
  '[force_reconnect]',
  '[ipc_timeout]',
  '[keychain]',
  '[Keychain]',
  '[NET]',
  '[note_defaults_client]',
  '[note_trigger]',
  '[OTA]',
  '[preflight]',
  '[Reconnect]',
  '[recover_pending]',
  '[recovered_stop]',
  '[retry_failed]',
  '[supervisor]',
  '[tier_b]',
  '[uploader]',
  '[USB]',
  '[WifiFileTransfer]',
  '[WifiTransferManager]',
];

const RUST_DEV_LOG_LEVEL_PREFIX = /^\[(TRACE|DEBUG|INFO|WARN|ERROR)\]\s/;

/**
 * RPA telemetry lines carry machine tokens, counts, and timings as
 * `key=value` pairs (see `tauri-plugin-rpa/src/telemetry.rs`).
 */
const RPA_PREFIX = '[rpa]';

/**
 * Parse a Rust tracing line's flattened `key=value` / `key="value"` fields so
 * they become queryable Datadog attributes instead of message text.
 */
export const parseRustLogFields = (
  message: string
): Record<string, string | number | boolean> => {
  const fields: Record<string, string | number | boolean> = {};
  const pattern = /(\w+)=(?:"((?:[^"\\]|\\.)*)"|(\S+))/g;
  for (const match of message.matchAll(pattern)) {
    const [, key, quoted, bare] = match;
    if (key === undefined) continue;
    if (quoted !== undefined) {
      fields[key] = quoted;
    } else if (bare === undefined) {
      continue;
    } else if (bare === 'true' || bare === 'false') {
      fields[key] = bare === 'true';
    } else if (!Number.isNaN(Number(bare))) {
      fields[key] = Number(bare);
    } else {
      fields[key] = bare;
    }
  }
  return fields;
};

/**
 * A Rust line that declares `tag="..."` is deliberate structured telemetry, so
 * its `key=value` pairs are worth promoting to Datadog attributes. Parsing
 * every line instead would mine nested pairs out of prose — `[BLE]` scan
 * results carry `seen=[{id=…, name=…}]` — producing high-cardinality junk
 * attributes from neighbouring devices.
 */
const TELEMETRY_TAG_FIELD = /(?:^|\s)tag="[^"]+"/;

const withParsedFields = (rustLogger: RustLogger, message: string): RustLogger =>
  TELEMETRY_TAG_FIELD.test(message)
    ? rustLogger.child(parseRustLogFields(message))
    : rustLogger;

const RUST_ENVELOPE_FIELD_MAP = {
  file: 'rust_file',
  line: 'rust_line',
  target: 'rust_target',
  module_path: 'rust_module_path',
} as const;

type ParsedRustLogEnvelope = {
  displayMessage: string;
  envelopeFields: Record<string, string | number>;
  /** The tracing target, when the line's shape carries one. */
  target: string | null;
};

/**
 * Rust logs arrive in three shapes depending on how the app is running: the raw
 * tracing message, a human-readable development line, or a JSON envelope in
 * packaged builds. Returns the inner message used for routing and display, plus
 * the per-log metadata lifted out of a packaged envelope. Parses a line at most
 * once — this runs on every log line inside the `attachLogger` callback.
 */
const parseRustLogEnvelope = (message: string): ParsedRustLogEnvelope => {
  if (message.startsWith('{')) {
    try {
      const parsed = JSON.parse(message) as Record<string, unknown>;
      const envelopeFields: Record<string, string | number> = {};
      for (const [sourceKey, destKey] of Object.entries(RUST_ENVELOPE_FIELD_MAP)) {
        const value = parsed[sourceKey];
        if (typeof value === 'number') {
          envelopeFields[destKey] = value;
        } else if (typeof value === 'string' && value !== '') {
          envelopeFields[destKey] = value;
        }
      }
      return {
        displayMessage: typeof parsed.message === 'string' ? parsed.message : message,
        envelopeFields,
        target: typeof parsed.target === 'string' ? parsed.target : null,
      };
    } catch {
      // A malformed JSON-looking line remains a normal Rust log.
      return { displayMessage: message, envelopeFields: {}, target: null };
    }
  }

  if (RUST_DEV_LOG_LEVEL_PREFIX.test(message)) {
    const messageSeparator = message.indexOf(': ');
    if (messageSeparator !== -1) {
      const levelPrefixLength = message.indexOf(']') + 2;
      const target = message.slice(levelPrefixLength, messageSeparator);
      return {
        displayMessage: message.slice(messageSeparator + 2),
        envelopeFields: {},
        // A dev line's target segment may itself be a bracketed message prefix
        // (`[INFO] [offline_recording::sync] …`), which is not a target.
        target: target.startsWith('[') ? null : target || null,
      };
    }
  }

  return { displayMessage: message, envelopeFields: {}, target: null };
};

/**
 * Tracing targets routed by MODULE PATH rather than by a message prefix.
 *
 * A prefix census (`rg -o '"\[[^"]+\]'`) cannot see a module that logs with
 * bare structured tracing — no `[Bracket]` in the message at all — so those
 * modules read as free-form and their sub-warn lines are dropped from
 * transport. `tauri-plugin-chronicle/src/wifi` is exactly that shape: ~34
 * sub-warn sites across setup / connection / downloader / tcp_client, none
 * bracketed, including the SSID-scrubbed WiFi transfer breadcrumbs that an
 * earlier privacy change deliberately kept at info.
 *
 * Matching the target fixes the whole class at once and needs no Rust change,
 * where adding a prefix would fix only the sites someone remembered to edit
 * and leave the next bare-tracing module to be discovered the same way.
 */
const RUST_TARGET_MODULE_ROUTES = ['tauri_plugin_chronicle'] as const;

const matchesChronicleTarget = (target: string | null): boolean =>
  target !== null &&
  RUST_TARGET_MODULE_ROUTES.some(
    (moduleTarget) => target === moduleTarget || target.startsWith(`${moduleTarget}::`)
  );

const withEnvelopeFields = (
  rustLogger: RustLogger,
  envelopeFields: Record<string, string | number>
): RustLogger =>
  Object.keys(envelopeFields).length > 0 ? rustLogger.child(envelopeFields) : rustLogger;

const withChronicleLoggerContext = (
  rustLogger: RustLogger,
  message: string,
  envelopeFields: Record<string, string | number>
): RustLogger =>
  withParsedFields(withEnvelopeFields(rustLogger, envelopeFields), message);

const getRustLogTarget = (
  message: string,
  loggers: {
    tauriRustLogger: RustLogger;
    tauriDictationLogger: RustLogger;
    tauriOfflineRecordingLogger: RustLogger;
    tauriRpaLogger: RustLogger;
    tauriChronicleLogger: RustLogger;
  }
): {
  logMessage: string;
  rustLogger: RustLogger | null;
  isChronicle: boolean;
  /** Set only on the free-form route, where the caller picks the logger. */
  envelopeFields?: Record<string, string | number>;
} => {
  const {
    displayMessage: messageForRouting,
    envelopeFields,
    target,
  } = parseRustLogEnvelope(message);

  if (messageForRouting.startsWith(DICTATION_PREFIX)) {
    return {
      logMessage: messageForRouting.slice(DICTATION_PREFIX.length).trimStart(),
      rustLogger: withParsedFields(loggers.tauriDictationLogger, messageForRouting),
      isChronicle: false,
    };
  }

  if (messageForRouting.startsWith(RPA_PREFIX)) {
    return {
      logMessage: messageForRouting,
      rustLogger: loggers.tauriRpaLogger.child(parseRustLogFields(messageForRouting)),
      isChronicle: false,
    };
  }

  if (isOfflineRecordingMessage(messageForRouting)) {
    return {
      logMessage: messageForRouting,
      rustLogger: withParsedFields(
        withEnvelopeFields(loggers.tauriOfflineRecordingLogger, envelopeFields),
        messageForRouting
      ),
      isChronicle: false,
    };
  }

  if (CHRONICLE_PREFIXES.some((prefix) => messageForRouting.startsWith(prefix))) {
    return {
      logMessage: messageForRouting,
      rustLogger: withChronicleLoggerContext(
        loggers.tauriChronicleLogger,
        messageForRouting,
        envelopeFields
      ),
      isChronicle: true,
    };
  }

  // Bare structured tracing from a chronicle module: no bracket prefix in the
  // message, so only the target identifies it. Without this it reads as
  // free-form and its sub-warn lines are dropped.
  if (matchesChronicleTarget(target)) {
    return {
      logMessage: messageForRouting,
      rustLogger: withChronicleLoggerContext(
        loggers.tauriChronicleLogger,
        messageForRouting,
        envelopeFields
      ),
      isChronicle: true,
    };
  }

  if (TELEMETRY_TAG_FIELD.test(messageForRouting)) {
    return {
      logMessage: messageForRouting,
      rustLogger: withEnvelopeFields(loggers.tauriRustLogger, envelopeFields).child(
        parseRustLogFields(messageForRouting)
      ),
      isChronicle: false,
    };
  }

  // Free-form: no module prefix and no tag= telemetry. The caller decides
  // whether to forward or drop it by level.
  return {
    logMessage: messageForRouting,
    rustLogger: null,
    isChronicle: false,
    envelopeFields,
  };
};

const isOfflineRecordingMessage = (messageForRouting: string): boolean => {
  return OFFLINE_RECORDING_PREFIXES.some((prefix) =>
    messageForRouting.startsWith(prefix)
  );
};
